import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Percent as PercentIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import NumericField from './NumericField';

const isActiveVendor = (entity) => (
  entity?.entityType === 'vendor' && String(entity?.status || '').toLowerCase() === 'active'
);

const compareValues = (left, right) => {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (amount) => currencyFormatter.format(Number(amount) || 0);

const getFrequencyColor = (frequency) => {
  switch (frequency) {
    case 'one_time': return 'info';
    case 'monthly': return 'success';
    case 'yearly': return 'warning';
    default: return 'default';
  }
};

const ServicesTable = React.memo(function ServicesTable({
  loading,
  rows,
  order,
  orderBy,
  onRequestSort,
  onEdit,
  onDelete,
}) {
  const columns = [
    { id: 'name', label: 'Service' },
    { id: 'entityName', label: 'Entity' },
    { id: 'categoryLabel', label: 'Category' },
    { id: 'locationLabel', label: 'Location' },
    { id: 'frequencyLabel', label: 'Frequency' },
    { id: 'internalCost', label: 'Cost' },
    { id: 'marginPercent', label: 'Margin' },
    { id: 'clientPrice', label: 'Price' },
  ];

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => onRequestSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No services found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.cost.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {row.name}
                        </Typography>
                        {row.cost.description && (
                          <Typography variant="caption" color="text.secondary">
                            {row.cost.description.slice(0, 130)}{row.cost.description.length > 130 ? '…' : ''}
                          </Typography>
                        )}
                        {row.cost.internal_note && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Internal Note: {row.cost.internal_note}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      {row.entityName}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.categoryLabel} color="default" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      {row.locationLabel}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.frequencyLabel}
                      color={row.frequencyColor}
                      size="small"
                      icon={<ScheduleIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{row.internalCostLabel}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${row.marginPercent}%`}
                      color="primary"
                      size="small"
                      icon={<PercentIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {row.clientPriceLabel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(row.cost)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete(row.cost.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
});

export default function Costs() {
  const location = useLocation();
  const { isMember } = useAuth();
  const { 
    getCostCategoryOptions, 
    getExecutionLocationOptions, 
    getFrequencyOptions,
    getCountryOptions,
    loading: configLoading 
  } = useConfig();

  // Get dynamic configuration options
  const categories = useMemo(() => getCostCategoryOptions(), [getCostCategoryOptions]);
  const executionLocations = useMemo(() => getExecutionLocationOptions(), [getExecutionLocationOptions]);
  const frequencies = useMemo(() => getFrequencyOptions(), [getFrequencyOptions]);
  const countries = useMemo(() => getCountryOptions(), [getCountryOptions]);
  const allowedFrequencies = useMemo(
    () => frequencies.filter(f => f.id === 'one_time' || f.id === 'monthly'),
    [frequencies]
  );

  // All useState hooks must come before any conditional returns
  const [costs, setCosts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    search: '',
    frequency: '',
    category: '',
    executionLocation: '',
    entity: ''
  });
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    internal_note: '',
    category: '',
    entity_id: '',
    execution_location: 'global',
    frequency: 'one_time',
    internal_cost: 0,
    margin_percent: 20,
    advertising_budget: 0,
    advertising_budget_frequency: 'one_time',
    advertising_budget_enabled: false,
    external_fee: 0,
    external_fee_frequency: 'one_time',
    external_fee_enabled: false,
  });

  const activeVendors = useMemo(
    () => entities
      .filter(isActiveVendor)
      .sort((a, b) => compareValues(a.entityName, b.entityName)),
    [entities]
  );

  const selectedVendor = useMemo(
    () => activeVendors.find(entity => entity.id === formData.entity_id) || null,
    [activeVendors, formData.entity_id]
  );

  const entityById = useMemo(
    () => new Map(entities.map(entity => [entity.id, entity])),
    [entities]
  );
  const categoryById = useMemo(
    () => new Map(categories.map(category => [category.id, category.name])),
    [categories]
  );
  const locationById = useMemo(
    () => new Map(executionLocations.map(item => [item.id, item.name])),
    [executionLocations]
  );
  const frequencyById = useMemo(
    () => new Map(frequencies.map(item => [item.id, item.name])),
    [frequencies]
  );
  const countryByCode = useMemo(
    () => new Map(countries.map(country => [country.code, country])),
    [countries]
  );



  const addCost = async (costData) => {
    try {
      setSaving(true);
      
      // Validation
      if (!costData.name.trim()) {
        throw new Error('Service name is required');
      }
      if (!costData.category) {
        throw new Error('Category is required');
      }
      if (!entities.some(entity => entity.id === costData.entity_id && isActiveVendor(entity))) {
        throw new Error('An active vendor is required');
      }
      if (costData.internal_cost <= 0) {
        throw new Error('Internal cost must be greater than 0');
      }

      const docRef = await addDoc(collection(db, 'costs'), {
        ...costData,
        internal_cost: Number(costData.internal_cost) || 0,
        margin_percent: Number(costData.margin_percent) || 0,
        advertising_budget: Number(costData.advertising_budget) || 0,
        advertising_budget_enabled: Boolean(costData.advertising_budget_enabled),
        external_fee: Number(costData.external_fee) || 0,
        external_fee_enabled: Boolean(costData.external_fee_enabled),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: 'current_user' // TODO: Replace with actual user ID
      });
      
      setSnackbar({ open: true, message: 'Cost added successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding cost:', error);
      setSnackbar({ open: true, message: error.message || 'Error adding cost', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateCost = async (costId, costData) => {
    try {
      setSaving(true);
      
      // Validation
      if (!costData.name.trim()) {
        throw new Error('Service name is required');
      }
      if (!costData.category) {
        throw new Error('Category is required');
      }
      if (!entities.some(entity => entity.id === costData.entity_id && isActiveVendor(entity))) {
        throw new Error('An active vendor is required');
      }
      if (costData.internal_cost <= 0) {
        throw new Error('Internal cost must be greater than 0');
      }

      const costRef = doc(db, 'costs', costId);
      await updateDoc(costRef, {
        ...costData,
        internal_cost: Number(costData.internal_cost) || 0,
        margin_percent: Number(costData.margin_percent) || 0,
        advertising_budget: Number(costData.advertising_budget) || 0,
        advertising_budget_enabled: Boolean(costData.advertising_budget_enabled),
        external_fee: Number(costData.external_fee) || 0,
        external_fee_enabled: Boolean(costData.external_fee_enabled),
        updated_at: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Cost updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating cost:', error);
      setSnackbar({ open: true, message: error.message || 'Error updating cost', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteCost = useCallback(async (costId) => {
    try {
      const costRef = doc(db, 'costs', costId);
      await deleteDoc(costRef);
      setSnackbar({ open: true, message: 'Cost deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting cost:', error);
      setSnackbar({ open: true, message: 'Error deleting cost', severity: 'error' });
      throw error;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubscribeCosts = onSnapshot(
      query(collection(db, 'costs'), orderBy('name', 'asc')),
      (querySnapshot) => {
        const costsData = querySnapshot.docs.map((snapshot) => {
          const data = snapshot.data();
          return {
            id: snapshot.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
            updated_at: data.updated_at?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
          };
        });
        setCosts(costsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching costs:', error);
        setSnackbar({ open: true, message: 'Error loading services', severity: 'error' });
        setLoading(false);
      }
    );
    const unsubscribeEntities = onSnapshot(
      query(collection(db, 'entities'), orderBy('entityName', 'asc')),
      (querySnapshot) => {
        setEntities(querySnapshot.docs.map(snapshot => ({ id: snapshot.id, ...snapshot.data() })));
      },
      (error) => {
        console.error('Error fetching entities:', error);
        setSnackbar({ open: true, message: 'Error loading vendors', severity: 'error' });
      }
    );

    return () => {
      unsubscribeCosts();
      unsubscribeEntities();
    };
  }, []);

  const filteredCosts = useMemo(() => {
    const search = filters.search.trim().toLocaleLowerCase();
    return costs.filter((cost) => {
      if (search && !String(cost.name || '').toLocaleLowerCase().includes(search)) return false;
      if (filters.frequency && cost.frequency !== filters.frequency) return false;
      if (filters.category && cost.category !== filters.category) return false;
      if (filters.executionLocation && cost.execution_location !== filters.executionLocation) return false;
      if (filters.entity && cost.entity_id !== filters.entity) return false;
      return true;
    });
  }, [costs, filters]);

  const serviceRows = useMemo(() => filteredCosts
    .map((cost, index) => {
      const entity = entityById.get(cost.entity_id);
      const internalCost = Number(cost.internal_cost) || 0;
      const marginPercent = Number(cost.margin_percent) || 0;
      const transportPercent = Math.max(0, Number(countryByCode.get(entity?.country)?.transportPercent) || 0);
      const clientPrice = internalCost * (1 + marginPercent / 100) + internalCost * (transportPercent / 100);
      return {
        cost,
        index,
        name: cost.name || '',
        entityName: entity?.entityName || cost.entity_id || '',
        categoryLabel: categoryById.get(cost.category) || cost.category || '',
        locationLabel: locationById.get(cost.execution_location) || cost.execution_location || '',
        frequencyLabel: frequencyById.get(cost.frequency) || cost.frequency || '',
        frequencyColor: getFrequencyColor(cost.frequency),
        internalCost,
        internalCostLabel: formatCurrency(internalCost),
        marginPercent,
        clientPrice,
        clientPriceLabel: formatCurrency(clientPrice),
      };
    })
    .sort((left, right) => {
      const result = compareValues(left[sortField], right[sortField]);
      return result === 0 ? left.index - right.index : result * (sortDirection === 'asc' ? 1 : -1);
    }), [
      filteredCosts,
      entityById,
      categoryById,
      locationById,
      frequencyById,
      countryByCode,
      sortDirection,
      sortField,
    ]);

  const handleOpenDialog = useCallback((cost = null) => {
    if (cost) {
      setEditingCost(cost);
      setFormData({
        name: cost.name,
        description: cost.description || '',
        internal_note: cost.internal_note || '',
        category: cost.category || '',
        entity_id: cost.entity_id || '',
        execution_location: cost.execution_location || 'global',
        frequency: (cost.frequency === 'yearly' ? 'monthly' : (cost.frequency || 'one_time')),
        internal_cost: cost.internal_cost || 0,
        margin_percent: cost.margin_percent || 20,
        advertising_budget: cost.advertising_budget || 0,
        advertising_budget_frequency: cost.advertising_budget_frequency || 'one_time',
        advertising_budget_enabled: Boolean(cost.advertising_budget_enabled),
        external_fee: cost.external_fee || 0,
        external_fee_frequency: cost.external_fee_frequency || 'one_time',
        external_fee_enabled: Boolean(cost.external_fee_enabled),
      });
    } else {
      setEditingCost(null);
      setFormData({
        name: '',
        description: '',
        internal_note: '',
        category: '',
        entity_id: '',
        execution_location: 'global',
        frequency: 'one_time',
        internal_cost: 0,
        margin_percent: 20,
        advertising_budget: 0,
        advertising_budget_frequency: 'one_time',
        advertising_budget_enabled: false,
        external_fee: 0,
        external_fee_frequency: 'one_time',
        external_fee_enabled: false,
      });
    }
    setOpenDialog(true);
  }, []);

  // Open "Add Service" dialog automatically when navigated with ?new=1.
  const newDialogOpenedRef = useRef(false);
  useEffect(() => {
    if (configLoading) return;
    try {
      const params = new URLSearchParams(location.search);
      if (!newDialogOpenedRef.current && params.get('new') === '1') {
        newDialogOpenedRef.current = true;
        handleOpenDialog();
      }
    } catch {}
  }, [location.search, configLoading, handleOpenDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCost(null);
  };

  const handleSubmit = async () => {
    try {
      const costData = {
        ...formData,
      };

      if (editingCost) {
        await updateCost(editingCost.id, costData);
      } else {
        await addCost(costData);
      }
      
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this cost?')) {
      try {
        await deleteCost(id);
      } catch (error) {
        // Error handling is done in the deleteCost function
      }
    }
  }, [deleteCost]);

  const handleRequestSort = useCallback((property) => {
    const isAscending = sortField === property && sortDirection === 'asc';
    setSortDirection(isAscending ? 'desc' : 'asc');
    setSortField(property);
  }, [sortDirection, sortField]);

  const getFrequencyLabel = (frequency) => {
    const freqOption = frequencies.find(f => f.id === frequency);
    return freqOption ? freqOption.name : frequency;
  };

  const getCategoryLabel = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  const getLocationLabel = (locationId) => {
    const location = executionLocations.find(l => l.id === locationId);
    return location ? location.name : locationId;
  };

  const getEntityLabel = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.entityName : entityId;
  };

  const getTransportPercentForCost = (cost) => {
    try {
      const entity = entities.find(e => e.id === cost.entity_id);
      if (!entity || !entity.country) return 0;
      const country = countries.find(c => c.code === entity.country);
      const percent = Number(country?.transportPercent) || 0;
      return percent < 0 ? 0 : percent;
    } catch {
      return 0;
    }
  };

  const calculateClientPrice = (cost) => {
    const internal = Number(cost.internal_cost) || 0;
    const marginPct = Number(cost.margin_percent) || 0;
    const transportPct = getTransportPercentForCost(cost);
    const baseWithMargin = internal * (1 + (marginPct / 100));
    const transportFee = internal * (transportPct / 100);
    // Transport fee is added on top (not marginable)
    return baseWithMargin + transportFee;
  };

  // Don't render form if config is still loading
  if (configLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handleExportCsv = () => {
    if (isMember()) return;
    try {
      const rows = filteredCosts || [];
      if (!rows.length) {
        setSnackbar({ open: true, message: 'No services to export', severity: 'info' });
        return;
      }

      const headers = [
        'Service',
        'Entity',
        'Category',
        'Location',
        'Frequency',
        'Internal Cost',
        'Margin %',
        'Client Price',
        'Ad Budget',
        'Ad Budget Frequency',
        'External Fee',
        'External Fee Frequency',
        'Description',
        'ID',
        'Created At',
        'Updated At',
      ];

      const escapeCsvValue = (value) => {
        const str = String(value ?? '');
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const dataRows = rows.map((cost) => {
        const internal = Number(cost.internal_cost ?? 0);
        const clientPrice = Number(calculateClientPrice(cost) ?? 0);
        return [
          cost.name || '',
          getEntityLabel(cost.entity_id),
          getCategoryLabel(cost.category),
          getLocationLabel(cost.execution_location),
          getFrequencyLabel(cost.frequency),
          internal.toFixed(2),
          Number(cost.margin_percent ?? 0).toString(),
          clientPrice.toFixed(2),
          Number(cost.advertising_budget ?? 0).toFixed(2),
          cost.advertising_budget_frequency ? getFrequencyLabel(cost.advertising_budget_frequency) : '',
          Number(cost.external_fee ?? 0).toFixed(2),
          cost.external_fee_frequency ? getFrequencyLabel(cost.external_fee_frequency) : '',
          cost.description || '',
          cost.id || '',
          cost.created_at || '',
          cost.updated_at || '',
        ];
      });

      const csv = [headers, ...dataRows]
        .map((row) => row.map(escapeCsvValue).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `services-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'CSV downloaded', severity: 'success' });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setSnackbar({ open: true, message: 'Error exporting CSV', severity: 'error' });
    }
  };

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Services
            </Typography>
            
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!isMember() && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Service
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="search"
              label="Search services"
              placeholder="Search by service name"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={filters.entity}
                label="Vendor"
                onChange={(e) => setFilters(prev => ({ ...prev, entity: e.target.value }))}
              >
                <MenuItem value="">All Active Vendors</MenuItem>
                {activeVendors.map(entity => (
                  <MenuItem key={entity.id} value={entity.id}>{entity.entityName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={filters.frequency}
                label="Frequency"
                onChange={(e) => setFilters(prev => ({ ...prev, frequency: e.target.value }))}
              >
                <MenuItem value="">All Frequencies</MenuItem>
                {allowedFrequencies.map(frequency => (
                  <MenuItem key={frequency.id} value={frequency.id}>{frequency.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Execution Location</InputLabel>
              <Select
                value={filters.executionLocation}
                label="Execution Location"
                onChange={(e) => setFilters(prev => ({ ...prev, executionLocation: e.target.value }))}
              >
                <MenuItem value="">All Locations</MenuItem>
                {executionLocations.map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <ServicesTable
        loading={loading}
        rows={serviceRows}
        order={sortDirection}
        orderBy={sortField}
        onRequestSort={handleRequestSort}
        onEdit={handleOpenDialog}
        onDelete={handleDelete}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCost ? 'Edit Service' : 'Add Service'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Landing Page UX Audit"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={5}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional internal explanation of what this cost includes..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Internal Note"
                multiline
                rows={3}
                value={formData.internal_note}
                onChange={(e) => setFormData({ ...formData, internal_note: e.target.value })}
                placeholder="Optional note for internal use only"
                helperText="Not shared externally; stays within the service list."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={activeVendors}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  return option.entityName || '';
                }}
                value={selectedVendor}
                onChange={(e, newValue) => setFormData({ ...formData, entity_id: newValue ? newValue.id : '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Active Vendor"
                    required
                    helperText="Only active vendors can be assigned to services."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableClearable={false}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name} - {category.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
                
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label="Margin %"
                value={formData.margin_percent}
                onValueChange={(n) => setFormData({ ...formData, margin_percent: n ?? 0 })}
                mode="decimal"
                decimals={1}
                suffix="%"
                min={0}
                max={500}
                helperText="Markup applied to internal cost"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Execution Location</InputLabel>
                <Select
                  value={formData.execution_location}
                  label="Execution Location"
                  onChange={(e) => setFormData({ ...formData, execution_location: e.target.value })}
                >
                  {executionLocations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name} - {location.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Pricing Structure */}
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label="Cost"
                value={formData.internal_cost}
                onValueChange={(n) => setFormData({ ...formData, internal_cost: n ?? 0 })}
                mode="decimal"
                decimals={2}
                min={0}
                prefix="$"
                helperText="What it costs us to execute"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                >
                    {allowedFrequencies.map(frequency => (
                    <MenuItem key={frequency.id} value={frequency.id}>
                      {frequency.name} - {frequency.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
        
            
            {/* Client-facing Fees */}
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label="Advertising Budget"
                value={formData.advertising_budget}
                onValueChange={(n) => setFormData({ ...formData, advertising_budget: n ?? 0 })}
                mode="decimal"
                decimals={2}
                min={0}
                prefix="$"
                helperText="Recommended client ad spend (FYI only)"
                disabled={!formData.advertising_budget_enabled}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ad Budget Frequency</InputLabel>
                <Select
                  value={formData.advertising_budget_frequency}
                  label="Ad Budget Frequency"
                  onChange={(e) => setFormData({ ...formData, advertising_budget_frequency: e.target.value })}
                  disabled={!formData.advertising_budget_enabled}
                >
                  {allowedFrequencies.map(frequency => (
                    <MenuItem key={frequency.id} value={frequency.id}>
                      {frequency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label="External Fee"
                value={formData.external_fee}
                onValueChange={(n) => setFormData({ ...formData, external_fee: n ?? 0 })}
                mode="decimal"
                decimals={2}
                min={0}
                prefix="$"
                helperText="3rd-party fee passed to client (e.g., GoDaddy)"
                disabled={!formData.external_fee_enabled}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>External Fee Frequency</InputLabel>
                <Select
                  value={formData.external_fee_frequency}
                  label="External Fee Frequency"
                  onChange={(e) => setFormData({ ...formData, external_fee_frequency: e.target.value })}
                  disabled={!formData.external_fee_enabled}
                >
                  {allowedFrequencies.map(frequency => (
                    <MenuItem key={frequency.id} value={frequency.id}>
                      {frequency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.advertising_budget_enabled}
                    onChange={(e) => setFormData({ ...formData, advertising_budget_enabled: e.target.checked })}
                  />
                }
                label="Enable Advertising Budget"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.external_fee_enabled}
                    onChange={(e) => setFormData({ ...formData, external_fee_enabled: e.target.checked })}
                  />
                }
                label="Enable External Fee"
              />
            </Grid>
            
            {/* Preview */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Cost Preview</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Internal Cost:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(formData.internal_cost || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Client Price (incl. Transport):</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {(() => {
                        const internal = Number(formData.internal_cost) || 0;
                        const marginPct = Number(formData.margin_percent) || 0;
                        const entity = entities.find(e => e.id === formData.entity_id);
                        const country = countries.find(c => c.code === (entity?.country || ''));
                        const transportPct = Number(country?.transportPercent) || 0;
                        const baseWithMargin = internal * (1 + marginPct / 100);
                        const transportFee = internal * (transportPct / 100);
                        return formatCurrency(baseWithMargin + transportFee);
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transport {(() => {
                        const entity = entities.find(e => e.id === formData.entity_id);
                        const country = countries.find(c => c.code === (entity?.country || ''));
                        const transportPct = Number(country?.transportPercent) || 0;
                        return `${transportPct}%`;
                      })()} based on vendor country
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (editingCost ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

    

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Backdrop for loading/saving */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}
